import React, { useState, useEffect } from 'react';
import { D, st, apiPut, apiPost, apiGet, apiDel, DEFAULT_PAYMENT_TERMS, DEFAULT_SCOPE_NOTE } from '../constants';
import { startRegistration } from '@simplewebauthn/browser';
import SupplyHousePricing from './SupplyHousePricing';

export default function Settings({ markup, setMarkup, me, companyInfo, setCompanyInfo, matPrices, setMatPrices, matPriceHistory, setMatPriceHistory, apiSaveMatPrices }) {
  const isOwner = me?.user?.role === 'owner';
  const canManagePricing = isOwner || me?.user?.role === 'foreman';

  const [co, setCo] = useState({ name:'', phone:'', address:'', licenseNum:'', billingEmail:'' });
  const [coSaving, setCoSaving] = useState(false);
  const [coMsg, setCoMsg] = useState('');

  const [rates, setRates] = useState({
    masterRate:125, journeymanRate:95, apprenticeRate:65,
    Materials:20, overhead:15, profit:10, permitFee:500, referralDiscountPct:10
  });
  const [ratesSaving, setRatesSaving] = useState(false);
  const [ratesMsg, setRatesMsg] = useState('');

  // Separate from `rates` on purpose: saveRates() coerces every value with
  // Number(v)||0, which would silently blank these strings.
  const [terms, setTerms] = useState({ paymentTerms:'', scopeNote:'', lateFeeEnabled:false });
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsSaved, setTermsSaved] = useState(false);

  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinMsg, setPinMsg] = useState('');
  const [pinErr, setPinErr] = useState('');

  const [team, setTeam] = useState(null);
  const [teamMsg, setTeamMsg] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPinValue, setResetPinValue] = useState('');
  const [teamBusy, setTeamBusy] = useState(false);

  const loadTeam = () => { apiGet('/data/users').then(setTeam).catch(()=>{}); };
  useEffect(() => { if (isOwner) { loadTeam(); } }, []);

  const removeMember = async (u) => {
    if (!confirm(`Remove ${u.name} from your team? They will no longer be able to sign in.`)) return;
    setTeamBusy(true); setTeamMsg(null);
    try {
      await apiDel('/data/users/'+u.id);
      loadTeam();
      setTeamMsg({ ok:true, text:`${u.name} removed.` });
    } catch (e) {
      let msg = e.message; try { msg = JSON.parse(msg).error || msg; } catch {}
      setTeamMsg({ ok:false, text: msg });
    }
    setTeamBusy(false);
  };

  const submitResetPin = async () => {
    if (resetPinValue.length < 4) { setTeamMsg({ ok:false, text:'PIN must be at least 4 digits' }); return; }
    setTeamBusy(true); setTeamMsg(null);
    try {
      await apiPut('/data/users/'+resetTarget.id, { pin: resetPinValue });
      setResetTarget(null); setResetPinValue('');
      setTeamMsg({ ok:true, text:`PIN reset for ${resetTarget.name}.` });
    } catch (e) {
      let msg = e.message; try { msg = JSON.parse(msg).error || msg; } catch {}
      setTeamMsg({ ok:false, text: msg });
    }
    setTeamBusy(false);
  };

  const [bioCreds, setBioCreds] = useState(null);
  const [bioBusy, setBioBusy] = useState(false);
  const [bioMsg, setBioMsg] = useState(null);
  const biometricSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

  const loadBioCreds = () => apiGet('/webauthn/credentials').then(setBioCreds).catch(()=>setBioCreds([]));
  useEffect(() => { loadBioCreds(); }, []);

  const enrollBiometric = async () => {
    setBioBusy(true); setBioMsg(null);
    try {
      const options = await apiPost('/webauthn/register-options', {});
      const attestation = await startRegistration(options);
      await apiPost('/webauthn/register-verify', { ...attestation, deviceLabel: navigator.platform || 'This device' });
      setBioMsg({ ok:true, text:'Biometric login enabled on this device.' });
      loadBioCreds();
    } catch (e) {
      let msg = e.name === 'NotAllowedError' ? 'Enrollment was cancelled.' : (e.message || 'Could not enable biometric login');
      try { msg = JSON.parse(msg).error || msg; } catch {}
      setBioMsg({ ok:false, text: msg });
    }
    setBioBusy(false);
  };

  const removeBiometric = async (id) => {
    setBioBusy(true); setBioMsg(null);
    try {
      await apiDel('/webauthn/credentials/'+id);
      loadBioCreds();
    } catch (e) {
      setBioMsg({ ok:false, text: e.message || 'Could not remove device' });
    }
    setBioBusy(false);
  };

  const [stripeStatus, setStripeStatus] = useState(null);
  const [pm, setPmState] = useState({});
  const [pmSaving, setPmSaving] = useState(false);
  const [pmSaved, setPmSaved] = useState(false);
  const setPM = (k, v) => setPmState(x => ({ ...x, [k]: v }));
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeMsg, setStripeMsg] = useState(null);

  const [billingStatus, setBillingStatus] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingMsg, setBillingMsg] = useState(null);

  useEffect(() => {
    if (!isOwner) return;
    apiGet('/billing/status').then(r => { setBillingStatus(r); if(r.interval) setBillingInterval(r.interval); }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') { setBillingMsg({ ok:true, text:'Subscription activated!' }); setTimeout(()=>setBillingMsg(null),5000); }
  }, [isOwner]);

  const subscribe = async (planKey) => {
    setBillingBusy(true); setBillingMsg(null);
    try { const { checkoutUrl } = await apiPost('/billing/checkout', { plan: planKey }); window.location.href = checkoutUrl; }
    catch (e) { setBillingMsg({ ok:false, text: e.message || 'Checkout failed' }); setBillingBusy(false); }
  };

  const openPortal = async () => {
    setBillingBusy(true);
    try { const { portalUrl } = await apiPost('/billing/portal', {}); window.location.href = portalUrl; }
    catch (e) { setBillingMsg({ ok:false, text: e.message || 'Failed to open portal' }); setBillingBusy(false); }
  };

  useEffect(() => {
    if (!isOwner) { setStripeLoading(false); return; }
    apiGet('/data/settings').then(s => {
      // The API returns camelCase and already-parsed objects. This previously read
      // s.payment_methods (snake_case) inside a try/catch, so it silently fell back
      // to {} and saved payment methods never loaded onto this screen.
      setPmState(s.paymentMethods || {});
      setTerms({
        paymentTerms: s.paymentTerms || '',
        scopeNote: s.scopeNote || '',
        lateFeeEnabled: Boolean(s.lateFeeEnabled),
      });
    }).catch(()=>{});
    apiGet('/connect/status').then(setStripeStatus).catch(e => {
      let msg = e.message; try { msg = JSON.parse(msg).error || msg; } catch {}
      setStripeMsg({ ok:false, text: msg });
    }).finally(() => setStripeLoading(false));

    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'connected') setStripeMsg({ ok:true, text:'Stripe account connected!' });
    if (params.get('stripe') === 'error') setStripeMsg({ ok:false, text:'There was an error connecting your Stripe account.' });
    if (params.has('stripe')) {
      params.delete('stripe');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  const connectStripe = async () => {
    setStripeBusy(true); setStripeMsg(null);
    try {
      const r = await apiGet('/connect/onboard');
      window.location.href = r.url;
    } catch (e) {
      let msg = e.message || 'Failed to start Stripe onboarding';
      try { msg = JSON.parse(msg).error || msg; } catch {}
      setStripeMsg({ ok:false, text: msg });
      setStripeBusy(false);
    }
  };

  const disconnectStripe = async () => {
    if (!confirm('Disconnect your Stripe account? Clients will no longer be able to pay invoices online.')) return;
    setStripeBusy(true); setStripeMsg(null);
    try {
      await apiPost('/connect/disconnect', {});
      setStripeStatus({ connected:false });
      setStripeMsg({ ok:true, text:'Stripe account disconnected.' });
    } catch (e) {
      let msg = e.message || 'Failed to disconnect';
      try { msg = JSON.parse(msg).error || msg; } catch {}
      setStripeMsg({ ok:false, text: msg });
    }
    setStripeBusy(false);
  };

  const [testEmail, setTestEmail] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailMsg, setTestEmailMsg] = useState(null);

  const sendTestEmail = async () => {
    setTestEmailSending(true);
    setTestEmailMsg(null);
    try {
      const res = await apiPost('/email/test', { to: testEmail });
      setTestEmailMsg({ ok: true, text: res.message + (res.provider ? ` (provider: ${res.provider})` : '') });
    } catch (e) {
      let msg = e.message || 'Failed to send test email';
      try { msg = JSON.parse(msg).error || msg; } catch {}
      setTestEmailMsg({ ok: false, text: msg });
    }
    setTestEmailSending(false);
  };

  useEffect(() => {
    if (!companyInfo) return;
    setCo({
      name: companyInfo.name || '',
      phone: companyInfo.phone || '',
      address: companyInfo.address || '',
      licenseNum: companyInfo.licenseNum || companyInfo.license_num || '',
      billingEmail: companyInfo.billingEmail || companyInfo.billing_email || ''
    });
  }, [companyInfo?.name]);

  useEffect(() => {
    if (!markup) return;
    setRates(r => ({
      masterRate: markup.masterRate ?? r.masterRate,
      journeymanRate: markup.journeymanRate ?? r.journeymanRate,
      apprenticeRate: markup.apprenticeRate ?? markup.helperRate ?? r.apprenticeRate ?? 65,
      Materials: markup.Materials ?? r.Materials,
      overhead: markup.overhead ?? r.overhead,
      profit: markup.profit ?? r.profit,
      permitFee: markup.permitFee ?? r.permitFee,
      referralDiscountPct: markup.referralDiscountPct ?? r.referralDiscountPct,
      taxRate: markup.taxRate ?? r.taxRate ?? 0,
      matTaxRate: markup.matTaxRate ?? r.matTaxRate ?? 0,
    }));
  }, [markup]);

  const saveCompany = async () => {
    setCoSaving(true); setCoMsg('');
    try {
      await apiPut('/data/company', co);
      setCompanyInfo(prev => ({ ...prev, ...co }));
      setCoMsg('Saved');
    } catch (e) { setCoMsg('Error: ' + e.message); }
    setCoSaving(false);
    setTimeout(() => setCoMsg(''), 3000);
  };

  const saveRates = async () => {
    setRatesSaving(true); setRatesMsg('');
    try {
      const cleaned = Object.fromEntries(Object.entries(rates).map(([k,v]) => [k, Number(v) || 0]));
      await apiPut('/data/settings', { markup: cleaned });
      setMarkup(cleaned);
      setRates(cleaned);
      setRatesMsg('Saved');
    } catch (e) { setRatesMsg('Error: ' + e.message); }
    setRatesSaving(false);
    setTimeout(() => setRatesMsg(''), 3000);
  };

  const savePin = async () => {
    setPinErr('');
    if (newPin.length < 4) { setPinErr('PIN must be at least 4 digits'); return; }
    if (newPin !== confirmPin) { setPinErr('PINs do not match'); return; }
    setPinSaving(true);
    try {
      await apiPost('/auth/change-pin', { newPin });
      setPinMsg('PIN changed'); setNewPin(''); setConfirmPin('');
      setTimeout(() => setPinMsg(''), 3000);
    } catch (e) { setPinErr('Error: ' + e.message); }
    setPinSaving(false);
  };

  const inp = { ...st.input, width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:12, color:D.muted, marginBottom:4 };
  const fld = { marginBottom:14 };

  return (
    <div style={{ maxWidth:560 }}>
      <div style={{ ...st.h1, marginBottom:20 }}>Settings</div>

      <div style={{ ...st.card, marginBottom:20 }}>
        <div style={{ fontWeight:600, marginBottom:16 }}>Company Info</div>
        {[
          ['Company Name', 'name'],
          ['Phone', 'phone'],
          ['Address', 'address'],
          ['License Number', 'licenseNum'],
        ].map(([label, key]) => (
          <div key={key} style={fld}>
            <div style={lbl}>{label}</div>
            <input
              style={{ ...inp, opacity: !isOwner ? 0.6 : 1 }}
              value={co[key]}
              onChange={e => setCo(p => ({ ...p, [key]: e.target.value }))}
              disabled={!isOwner}
            />
          </div>
        ))}
        <div style={fld}>
          <div style={lbl}>Email</div>
          <input style={{ ...inp, opacity:0.6 }} value={companyInfo?.email || ''} readOnly />
        </div>
        <div style={fld}>
          <div style={lbl}>Billing Email</div>
          <input
            type="email"
            style={{ ...inp, opacity: !isOwner ? 0.6 : 1 }}
            placeholder={companyInfo?.email || 'you@example.com'}
            value={co.billingEmail}
            onChange={e => setCo(p => ({ ...p, billingEmail: e.target.value }))}
            disabled={!isOwner}
          />
          <div style={{ fontSize:11, color:D.muted, marginTop:4 }}>
            Replies to automated payment reminders will go here. Defaults to your account email if left blank.
          </div>
        </div>
        {isOwner && (
          <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:4 }}>
            <button onClick={saveCompany} disabled={coSaving} style={{ ...st.btn(), opacity:coSaving?0.6:1 }}>
              {coSaving ? 'Saving...' : 'Save'}
            </button>
            {coMsg && <span style={{ fontSize:13, color:coMsg.startsWith('Error')?D.danger:D.success }}>{coMsg}</span>}
          </div>
        )}
      </div>

      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Labor & Markup Rates</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
            {[
              ['Master Rate ($/hr)', 'masterRate'],
              ['Journeyman Rate ($/hr)', 'journeymanRate'],
              ['Apprentice Rate ($/hr)', 'apprenticeRate'],
              ['Permit Fee ($)', 'permitFee'],
              ['Material Markup (%)', 'Materials'],
              ['Overhead (%)', 'overhead'],
              ['Profit (%)', 'profit'],
              ['Referral Discount (%)', 'referralDiscountPct'],
              ['Sales Tax CHARGED to customer (%)', 'taxRate'],
              ['Materials Sales Tax You PAY (%)', 'matTaxRate'],
            ].map(([label, key]) => (
              <div key={key} style={fld}>
                <div style={lbl}>{label}</div>
                <input
                  type="number" min="0"
                  style={inp}
                  value={rates[key] ?? 0}
                  onChange={e => setRates(r => ({ ...r, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:4 }}>
            <button onClick={saveRates} disabled={ratesSaving} style={{ ...st.btn(), opacity:ratesSaving?0.6:1 }}>
              {ratesSaving ? 'Saving...' : 'Save Rates'}
            </button>
            {ratesMsg && <span style={{ fontSize:13, color:ratesMsg.startsWith('Error')?D.danger:D.success }}>{ratesMsg}</span>}
          </div>
        </div>
      )}

      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontWeight:600 }}>Subscription</div>
            {billingStatus?.referralCode && <div style={{ fontSize:11, color:D.muted }}>Referral code: <strong style={{color:D.accent}}>{billingStatus.referralCode}</strong></div>}
          </div>
          {(!billingStatus || ['trial','expired'].includes(billingStatus.plan)) && <>
            <div style={{ fontSize:13, color:(billingStatus?.daysRemaining??1)>3?D.muted:D.danger, marginBottom:16 }}>
              {billingStatus?.daysRemaining>0 ? `⏱ ${billingStatus.daysRemaining} days remaining in your free trial` : '⚠️ Your free trial has ended — choose a plan to continue'}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {['monthly','annual'].map(iv=>(
                <button key={iv} onClick={()=>setBillingInterval(iv)} style={{...st.btn(),marginLeft:0,background:billingInterval===iv?D.accent:D.border,color:billingInterval===iv?'#fff':D.text,fontSize:12}}>
                  {iv==='monthly'?'Monthly':'Annual — 2 months free ⚡'}
                </button>
              ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
              {[{key:'solo',label:'Solo',monthly:49.99,seats:'1–2 seats'},{key:'crew',label:'Crew',monthly:99.99,seats:'Up to 6',highlight:true},{key:'team',label:'Team',monthly:179.99,seats:'Up to 12'}].map(pl=>{
                const planKey=billingInterval==='annual'?pl.key+'_annual':pl.key;
                const price=billingInterval==='annual'?pl.monthly*10:pl.monthly;
                return(<div key={pl.key} style={{border:`1px solid ${pl.highlight?D.accent:D.border}`,borderRadius:10,padding:'14px 12px',textAlign:'center',background:pl.highlight?D.accent+'11':D.surface}}>
                  <div style={{fontWeight:700,color:pl.highlight?D.accent:D.text}}>{pl.label}</div>
                  <div style={{fontSize:20,fontWeight:700,margin:'8px 0 2px'}}>${price.toFixed(2)}</div>
                  <div style={{fontSize:11,color:D.muted,marginBottom:12}}>{billingInterval==='annual'?'/year':'/month'} · {pl.seats}</div>
                  <button disabled={billingBusy} onClick={()=>subscribe(planKey)} style={{...st.btn('primary'),marginLeft:0,width:'100%',fontSize:11,padding:'7px 0',opacity:billingBusy?0.6:1}}>
                    {billingBusy?'Loading...':'Subscribe'}
                  </button>
                </div>);
              })}
            </div>
          </>}
          {billingStatus?.plan && !['trial','expired'].includes(billingStatus.plan) && (
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontWeight:600,color:D.success,textTransform:'capitalize'}}>{billingStatus.plan} Plan</div>
                <div style={{fontSize:12,color:D.muted}}>{billingStatus.cancelAtPeriodEnd?'Cancels':'Renews'}{billingStatus.currentPeriodEnd?' on '+new Date(billingStatus.currentPeriodEnd).toLocaleDateString():''}</div>
              </div>
              <button disabled={billingBusy} onClick={openPortal} style={{...st.btn(),opacity:billingBusy?0.6:1}}>{billingBusy?'Loading...':'Manage Subscription'}</button>
            </div>
          )}
          {billingMsg&&<div style={{marginTop:12,fontSize:12,color:billingMsg.ok?D.success:D.danger}}>{billingMsg.text}</div>}
        </div>
      )}

      {canManagePricing && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Supply House Pricing</div>
          <SupplyHousePricing matPrices={matPrices} setMatPrices={setMatPrices} matPriceHistory={matPriceHistory} setMatPriceHistory={setMatPriceHistory} apiSaveMatPrices={apiSaveMatPrices}/>
        </div>
      )}

      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Team</div>

          {!team ? (
            <div style={{ fontSize:13, color:D.muted }}>Loading...</div>
          ) : (
            <div>
              {team.map(u => (
                <div key={u.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${D.border}` }}>
                  <div>
                    <strong style={{ fontSize:13 }}>{u.name}</strong>
                    <span style={{ fontSize:11, color:D.muted, marginLeft:8 }}>{u.role}{u.email ? ` · ${u.email}` : ''}</span>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    {resetTarget?.id === u.id ? (
                      <>
                        <input style={{ ...inp, width:100, padding:'4px 8px' }} placeholder="New PIN" value={resetPinValue} onChange={e=>setResetPinValue(e.target.value)} autoFocus/>
                        <button onClick={submitResetPin} disabled={teamBusy} style={{ ...st.btn('primary'), fontSize:11, padding:'4px 10px' }}>Save</button>
                        <button onClick={()=>{setResetTarget(null);setResetPinValue('');}} style={{ ...st.btn(), fontSize:11, padding:'4px 10px' }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={()=>{setResetTarget(u);setResetPinValue('');}} style={{ ...st.btn(), fontSize:11, padding:'4px 10px' }}>Reset PIN</button>
                        <button onClick={()=>removeMember(u)} style={{ ...st.btn('danger'), fontSize:11, padding:'4px 10px' }}>Remove</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {teamMsg && <div style={{ marginTop:10, fontSize:13, color: teamMsg.ok ? D.success : D.danger }}>{teamMsg.text}</div>}
          <div style={{ marginTop:12, fontSize:12, color:D.muted }}>Add new crew members and assign trucks from the Truck page.</div>
        </div>
      )}

      <div style={{ ...st.card, marginBottom:20 }}>
        <div style={{ fontWeight:600, marginBottom:16 }}>Biometric Login</div>
        {!biometricSupported ? (
          <div style={{ fontSize:13, color:D.muted }}>Your browser doesn't support biometric login (Face ID / Fingerprint).</div>
        ) : !bioCreds ? (
          <div style={{ fontSize:13, color:D.muted }}>Loading...</div>
        ) : (
          <div>
            <div style={{ fontSize:13, color:D.muted, marginBottom:12 }}>
              Enable Face ID or fingerprint sign-in on this device instead of typing your PIN every time.
            </div>
            {bioCreds.length > 0 && (
              <div style={{ marginBottom:12 }}>
                {bioCreds.map(c => (
                  <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${D.border}` }}>
                    <span style={{ fontSize:13 }}>{c.device_label || 'Device'}</span>
                    <button onClick={()=>removeBiometric(c.id)} disabled={bioBusy} style={{ ...st.btn('danger'), fontSize:11, padding:'3px 10px' }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={enrollBiometric} disabled={bioBusy} style={{ ...st.btn('primary'), opacity:bioBusy?0.6:1 }}>
              {bioBusy ? 'Working...' : '+ Enable on this device'}
            </button>
          </div>
        )}
        {bioMsg && <div style={{ marginTop:10, fontSize:13, color: bioMsg.ok ? D.success : D.danger }}>{bioMsg.text}</div>}
      </div>


      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Invoice Terms &amp; Notes</div>
          <div style={{ fontSize:12, color:D.muted, marginBottom:14 }}>Printed at the bottom of every invoice, estimate and bid, and included in the emailed copy. Leave blank to use the default wording shown.</div>

          <div style={{ fontSize:12, color:D.text, marginBottom:5, fontWeight:500 }}>Payment Terms</div>
          <textarea style={{ ...st.input, width:'100%', minHeight:70, boxSizing:'border-box', fontFamily:'inherit', marginBottom:14 }}
            value={terms.paymentTerms} placeholder={DEFAULT_PAYMENT_TERMS}
            onChange={e=>setTerms(t=>({...t, paymentTerms:e.target.value}))}/>

          <div style={{ fontSize:12, color:D.text, marginBottom:5, fontWeight:500 }}>Scope Note</div>
          <textarea style={{ ...st.input, width:'100%', minHeight:70, boxSizing:'border-box', fontFamily:'inherit', marginBottom:14 }}
            value={terms.scopeNote} placeholder={DEFAULT_SCOPE_NOTE}
            onChange={e=>setTerms(t=>({...t, scopeNote:e.target.value}))}/>

          <div style={{ background:D.surface, borderRadius:8, padding:'10px 14px', border:`1px solid ${terms.lateFeeEnabled?D.accent:D.border}` }}>
            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
              <input type="checkbox" checked={terms.lateFeeEnabled} style={{width:16,height:16,accentColor:D.accent}}
                onChange={e=>setTerms(t=>({...t, lateFeeEnabled:e.target.checked}))}/>
              <span style={{ fontSize:13, fontWeight:500, color:D.text }}>Apply late fees automatically</span>
            </label>
            <div style={{ fontSize:11, color:D.muted, marginTop:6, lineHeight:1.5 }}>
              When on, invoices 30+ days overdue get a 1.5% late fee added as a line item, once a month. A warning email goes out 10 days before. You can exempt an individual invoice from its own screen. Off by default, because this changes an invoice the customer already has.
            </div>
          </div>

          <button style={{...st.btn('primary'),marginLeft:0,marginTop:12,opacity:termsSaving?0.6:1}} disabled={termsSaving} onClick={async()=>{
            setTermsSaving(true); setTermsSaved(false);
            await apiPut('/data/settings',{ paymentTerms:terms.paymentTerms, scopeNote:terms.scopeNote, lateFeeEnabled:terms.lateFeeEnabled }).catch(()=>{});
            // companyInfo is fetched once on app load, so update it in place -
            // otherwise the invoice keeps rendering the old terms until a reload.
            setCompanyInfo(ci=>({...ci, paymentTerms:terms.paymentTerms, scopeNote:terms.scopeNote}));
            setTermsSaving(false); setTermsSaved(true); setTimeout(()=>setTermsSaved(false),2500);
          }}>{termsSaving?'Saving...':termsSaved?'\u2713 Saved!':'Save'}</button>
        </div>
      )}
      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Payment Methods on Invoices</div>
          <div style={{ fontSize:12, color:D.muted, marginBottom:14 }}>These appear on every invoice and estimate you send to clients.</div>
          {[
            { key:'check', icon:'💵', label:'Check' },
            { key:'cash', icon:'💴', label:'Cash' },
            { key:'venmo', icon:'💙', label:'Venmo', handleKey:'venmoHandle', placeholder:'@yourhandle' },
            { key:'zelle', icon:'🟣', label:'Zelle', handleKey:'zelleContact', placeholder:'Phone or email' },
            { key:'paypal', icon:'🔵', label:'PayPal', handleKey:'paypalUser', placeholder:'Username or email' },
            { key:'cashapp', icon:'🟢', label:'Cash App', handleKey:'cashappTag', placeholder:'$cashtag' },
          ].map(opt => (
            <div key={opt.key} style={{background:D.surface,borderRadius:8,padding:'10px 14px',border:`1px solid ${pm[opt.key]?D.accent:D.border}`,marginBottom:8}}>
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',marginBottom:(pm[opt.key]&&opt.handleKey)?8:0}}>
                <input type="checkbox" checked={!!pm[opt.key]} onChange={e=>setPM(opt.key,e.target.checked)} style={{width:16,height:16,accentColor:D.accent}}/>
                <span style={{fontSize:13,fontWeight:500,color:D.text}}>{opt.icon} {opt.label}</span>
              </label>
              {opt.handleKey && pm[opt.key] && (
                <input style={{fontSize:12,background:D.bg,border:`1px solid ${D.border}`,borderRadius:6,padding:'6px 10px',width:'100%',boxSizing:'border-box',color:D.text}} value={pm[opt.handleKey]||''} onChange={e=>setPM(opt.handleKey,e.target.value)} placeholder={opt.placeholder}/>
              )}
            </div>
          ))}
          <button style={{...st.btn('primary'),marginLeft:0,marginTop:8,opacity:pmSaving?0.6:1}} disabled={pmSaving} onClick={async()=>{
            setPmSaving(true);setPmSaved(false);
            await apiPut('/data/settings',{paymentMethods:pm}).catch(()=>{});
            setPmSaving(false);setPmSaved(true);setTimeout(()=>setPmSaved(false),2500);
          }}>{pmSaving?'Saving...':pmSaved?'✓ Saved!':'Save'}</button>
        </div>
      )}
      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Online Payments</div>
          {stripeLoading ? (
            <div style={{ fontSize:13, color:D.muted }}>Loading...</div>
          ) : stripeStatus?.connected ? (
            <div>
              <div style={{ fontSize:13, marginBottom:8 }}>
                Connected as <strong>{stripeStatus.displayName || 'your Stripe account'}</strong>
              </div>
              <div style={{ fontSize:12, color:D.muted, marginBottom:12 }}>
                {stripeStatus.chargesEnabled
                  ? 'Clients can pay invoices online via the "Pay Online" link.'
                  : 'Setup incomplete — Stripe needs more information before you can accept payments.'}
              </div>
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                {!stripeStatus.chargesEnabled && (
                  <button onClick={connectStripe} disabled={stripeBusy} style={{ ...st.btn(), opacity:stripeBusy?0.6:1 }}>
                    {stripeBusy ? 'Loading...' : 'Complete Setup'}
                  </button>
                )}
                <button onClick={disconnectStripe} disabled={stripeBusy} style={{ ...st.btn('danger'), opacity:stripeBusy?0.6:1 }}>
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize:13, color:D.muted, marginBottom:12 }}>
                Connect a Stripe account to let clients pay invoices online with a card. Payments go directly to your bank account — AmpBooks never holds your funds.
              </div>
              <button onClick={connectStripe} disabled={stripeBusy} style={{ ...st.btn('primary'), opacity:stripeBusy?0.6:1 }}>
                {stripeBusy ? 'Loading...' : 'Connect with Stripe'}
              </button>
            </div>
          )}
          {stripeMsg && <div style={{ marginTop:10, fontSize:13, color: stripeMsg.ok ? D.success : D.danger }}>{stripeMsg.text}</div>}
        </div>
      )}

      {isOwner && (
        <div style={{ ...st.card, marginBottom:20 }}>
          <div style={{ fontWeight:600, marginBottom:16 }}>Test Email Delivery</div>
          <div style={fld}>
            <div style={lbl}>Send a test email to</div>
            <input
              type="email"
              style={{ ...inp, maxWidth:320 }}
              placeholder="you@example.com"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
            />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button onClick={sendTestEmail} disabled={testEmailSending || !testEmail} style={{ ...st.btn(), opacity:(testEmailSending || !testEmail)?0.6:1 }}>
              {testEmailSending ? 'Sending...' : 'Send Test Email'}
            </button>
            {testEmailMsg && <span style={{ fontSize:13, color: testEmailMsg.ok ? D.success : D.danger }}>{testEmailMsg.text}</span>}
          </div>
        </div>
      )}

      <div style={st.card}>
        <div style={{ fontWeight:600, marginBottom:16 }}>Change PIN</div>
        {pinErr && <div style={{ color:D.danger, marginBottom:10, fontSize:13 }}>{pinErr}</div>}
        <div style={fld}>
          <div style={lbl}>New PIN</div>
          <input type="password" style={{ ...inp, maxWidth:200 }} value={newPin} onChange={e=>setNewPin(e.target.value)} />
        </div>
        <div style={fld}>
          <div style={lbl}>Confirm PIN</div>
          <input type="password" style={{ ...inp, maxWidth:200 }} value={confirmPin} onChange={e=>setConfirmPin(e.target.value)} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={savePin} disabled={pinSaving} style={{ ...st.btn(), opacity:pinSaving?0.6:1 }}>
            {pinSaving ? 'Saving...' : 'Change PIN'}
          </button>
          {pinMsg && <span style={{ fontSize:13, color:D.success }}>{pinMsg}</span>}
        </div>
      </div>
    </div>
  );
}
